import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import { cooperplaceApi } from 'services/api';
import moment from 'moment';
import { CheckCircle, XCircle, AlertTriangle, FileText } from 'lucide-react';
import { useSnackbar } from 'v3/components/Snackbar';
import * as S from './styles';

const NI = 'Não informado';

export default function IntegrationSmModal({
  open,
  onClose,
  travelData,
  preSMData,
  smData,
  onRefresh,
  getLoad,
}) {
  const [loading, setLoading] = useState(false);
  const [loadingDataReady, setLoadingDataReady] = useState(false);
  const [step, setStep] = useState('INTEGRATION');
  const [smList, setSmList] = useState([]);
  const [selectedSM, setSelectedSM] = useState(null);
  const [smType, setSmType] = useState('risco');
  const [reloadSmInfo, setReloadSmInfo] = useState(false);
  const [smTypeError, setSmTypeError] = useState(false);
  const [visualizedSM, setVisualizedSM] = useState(null);

  const snackbar = useSnackbar();
  const travelEscort = travelData?.travelEscort || null;
  const shipper = travelData?.shipper;
  const shipperName = shipper?.socialName || NI;
  const shipperCnpj = shipper?.cpfCnpj || '';
  const shipperType = shipper?.descriptionServiceLevel || null;
  const shippingCompanyStatus = shipper?.shippingCompany?.descriptionServiceLevel || null;
  const productList = travelData?.products || [];
  const productDisplay = productList.length
    ? productList.map((p) => `${p.productType?.name || NI} ${p.ncm || ''}`).join(', ')
    : `${travelData?.loads?.productType?.name || NI} ${travelData?.loads?.product?.ncm || ''}`;
  const cargoValue = travelData?.loads?.cargoValue || null;
  const formattedCargoValue = cargoValue
    ? cargoValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : NI;
  const vehicleData = travelData?.vehicle?.[0];
  const tractionVehicle = vehicleData?.vehicles;
  const tractionType = travelData?.shipperProps?.linkShippingCompany?.name || null;
  const wagons = vehicleData?.wagons || [];
  const wagonPlates = wagons
    .map((w) => w?.plate)
    .filter(Boolean)
    .slice(0, 3);
  const wagonTypes = wagons.map((w) => w?.descriptionServiceLevel || null);
  const driver = travelData?.driver;
  const driverName = driver?.socialName || NI;
  const driverCpf = driver?.cpfCnpj || NI;
  const driverData = travelData?.driverData;
  const driverType = driverData?.descriptionServiceLevel || null;
  const manager = travelData?.user?.username || NI;
  const origins = travelData?.loads?.loadOrigins || [];
  const destinations = travelData?.loads?.loadDestinations || [];
  const baits = travelData?.baits || [];
  const escorts = travelData?.travelEscort || [];
  const escortVehicle = travelEscort?.vehicle || NI;
  const escortAgent =
    travelEscort?.agentName && travelEscort?.agentCpf
      ? `${travelEscort.agentName} – ${travelEscort.agentCpf}`
      : NI;
  const rasterBranch = travelData?.travelCostCenter?.rasterBranches?.[0]?.name || NI;
  const extractCityName = (point) => point?.city || point?.cityModel?.name || NI;
  const extractProvinceUF = (point) =>
    point?.province || point?.cityModel?.province?.uf || point?.province?.uf || NI;
  const determineSmType = (code) => {
    return code === 4145 ? 'logistica' : 'risco';
  };

  const getPrefixForAttempt = (status, isPre) => {
    if (status === 'Efetivada') {
      return 'SM';
    }
    return isPre ? 'Pré-SM' : 'SM';
  };

  useEffect(() => {
    if (!open || !travelData?.id || !preSMData?.logs) return;

    const fetchSmData = () => {
      const smList = filterBySolicitations(preSMData.logs);
      const sortedSmList = smList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const selected = sortedSmList[0] || null;
      updateSmState(smList, selected);
    };

    fetchSmData();
  }, [open, travelData?.id, preSMData, reloadSmInfo]);

  useEffect(() => {
    // Evita loop infinito: só seta como pronto se a modal estiver aberta
    if (open && preSMData?.logs) {
      setLoadingDataReady(true);
    }
  }, [open, smList, selectedSM, preSMData?.logs]);

  useEffect(() => {
    if (open && smList.length === 0 && selectedSM === null) {
      setLoadingDataReady(true);
    }
  }, [open, smList, selectedSM]);

  const renderBadge = (type) => {
    if (!type) return <></>;
    const normalized = type.toLowerCase();
    const badgeClass = normalized.includes('terceiro')
      ? 'badge-orange'
      : normalized.includes('frota')
      ? 'badge-green'
      : normalized.includes('agregado')
      ? 'badge-blue'
      : 'badge-default';
    return <S.Badge className={badgeClass}>{type}</S.Badge>;
  };

  const renderPointBlock = (label, point) => (
    <S.FieldWrapper>
      <div className="label">{label}</div>
      {point?.city?.name && point?.city?.province ? (
        <>
          <div className="value">
            {point.city.name} – {point.city.province || NI}
          </div>
          <div className="value">
            {point.start_schedule ? moment(point.start_schedule).format('DD/MM/YYYY HH:mm') : NI}
          </div>
          <div className="value">
            {point.scheduled_time ? moment(point.scheduled_time).format('DD/MM/YYYY HH:mm') : NI}
          </div>
        </>
      ) : (
        <div className="value">{NI}</div>
      )}
    </S.FieldWrapper>
  );

  const parsePointLabel = (action, index) => {
    if (!action) return `Entrega ${index + 1}`;
    const a = action.toLowerCase();
    if (a.includes('pickup')) return `Coleta ${index + 1}`;
    if (a.includes('custom')) return `Aduana ${index + 1}`;
    return `Entrega ${index + 1}`;
  };

  function filterBySolicitations(logs) {
    const statusToShow = [
      'CREATED_SM',
      'CREATED_PRE_SM',
      'FINISHED_SM',
      'CANCELLED_SM',
      'CANCELLED_PRE_SM',
      'EFFECT_PRE_SM',
    ];
    return logs
      .filter((log) => statusToShow.includes(log.statusPreSM?.slug))
      .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))
      .map((log) => {
        const status = getStatusFromLog(log.log_status);
        const type = determineSmType(travelData.securityProfile.code);
        const code = log.cod_sm != null ? log.cod_sm : log.cod_pre_sm || '';
        const isPre = log.statusPreSM?.slug.includes('PRE_SM');
        const createdBy = log.user?.username || 'Usuário não informado';
        const { createdAt } = log;

        return {
          ...log,
          status,
          type,
          code,
          isPre,
          createdBy,
          createdAt,
        };
      });
  }

  const getVerbForStatus = (status) => {
    switch (status) {
      case 'Solicitada':
        return 'Solicitada por';
      case 'Cancelada':
        return 'Cancelado por';
      case 'Efetivada':
        return 'Efetivado por';
      default:
        return 'Processado por';
    }
  };

  function updateSmState(smList, selected) {
    setSmList(smList);
    setSelectedSM(selected);
    setVisualizedSM(selected);
    setSmType(determineSmType(travelData?.securityProfile?.code));
  }

  const getStatusFromLog = (logStatus) => {
    const lower = logStatus.toLowerCase();
    if (lower.includes('cancelada')) return 'Cancelada';
    if (lower.includes('efetivada')) return 'Efetivada';
    if (lower.includes('solicitada')) return 'Solicitada';
    if (lower.includes('finalizada')) return 'Finalizada';
    return 'Desconhecido';
  };

  const getStatusColorClass = (status) => {
    const normalized = status?.toLowerCase();
    if (normalized.includes('efetivada')) return 'status-success';
    if (normalized.includes('cancelada')) return 'status-error';
    if (normalized.includes('solicitada')) return 'status-warning';
    if (normalized.includes('erro')) return 'status-error';
    if (normalized.includes('finalizada')) return 'status-success';
    return 'status-default';
  };

  const handleBackToIntegration = () => setStep('INTEGRATION');
  const handleGoToConfirmation = () => setStep('CONFIRM');

  const handleConfirmationComplete = async () => {
    setLoading(true);
    setSmTypeError(false);
    try {
      if (!travelData?.securityProfile?.code && travelData?.securityProfile?.code !== 0) {
        setSmTypeError(true);
        setLoading(false);
        return;
      }

      await cooperplaceApi.post('/presm', { id: travelData.id, tipo: smType });
      snackbar.show(<Text>Pré-SM enviada com sucesso.</Text>, { type: 'success' });
      const resp = await cooperplaceApi.get(`/presm/${travelData.id}`);
      const list = resp.data.presmList || [];

      setSmList(list);
      setSelectedSM(list.find((sm) => sm.status !== 'Cancelada') || null);
      const updatedLogs = resp.data?.logs || [];
      const selectedCode = list.find((sm) => sm.status !== 'Cancelada')?.code || '0';
      const filteredLogs = updatedLogs.filter(
        (log) => (log.cod_sm || log.cod_pre_sm || '0') === String(selectedCode),
      );

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.response?.data?.error || 'Erro ao solicitar SM';
      snackbar.show(<Text>Erro ao solicitar SM: {message}</Text>, { type: 'danger' });
      if (onRefresh) {
        await onRefresh();
      }
      setReloadSmInfo((prev) => !prev);
    } finally {
      setStep('INTEGRATION');
      setReloadSmInfo((prev) => !prev);
      setLoading(false);
    }
  };

  const handleEffectSm = async () => {
    setLoading(true);
    try {
      await cooperplaceApi.post(`/presm/effect`, { id: travelData.id });
      snackbar.show(<Text>SM efetivada com sucesso</Text>, { type: 'success' });

      const resp = await cooperplaceApi.get(`/presm/${travelData.id}`);
      const list = resp.data.presmList || [];
      setSmList(list);

      const latestSM = list.find((sm) => sm.status !== 'Cancelada') || list[0] || null;
      setSelectedSM(latestSM);

      const updatedLogs = resp.data?.logs || [];
      const logsOfSelected = updatedLogs.filter(
        (log) => (log.cod_sm || log.cod_pre_sm || '0') === String(latestSM?.code),
      );

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Erro ao finalizar SM';
      snackbar.show(<Text>Erro ao finalizar SM: {message}</Text>, { type: 'danger' });
      console.error('Erro ao finalizar SM:', err);
      if (onRefresh) {
        await onRefresh();
      }
      setReloadSmInfo((prev) => !prev);
    } finally {
      setStep('INTEGRATION');
      if (getLoad) await getLoad();
      setReloadSmInfo((prev) => !prev);
      setLoading(false);
    }
  };

  const handleFinalizeSm = async () => {
    setLoading(true);
    try {
      if (!travelData?.id) {
        snackbar.show(<Text>SM não encontrada para finalização.</Text>, { type: 'danger' });
        return;
      }

      await cooperplaceApi.post(`/presm/${travelData?.id}/finalize`);

      snackbar.show(<Text>SM finalizada com sucesso.</Text>, { type: 'success' });

      const resp = await cooperplaceApi.get(`/presm/${travelData?.id}`);
      const list = resp.data.presmList || [];
      setSmList(list);

      const latestSM = list.find((sm) => sm.status !== 'Cancelada') || list[0] || null;
      setSelectedSM(latestSM);

      const updatedLogs = resp.data?.logs || [];
      const logsOfSelected = updatedLogs.filter(
        (log) => (log.cod_sm || log.cod_pre_sm || '0') === String(latestSM?.code),
      );

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Erro ao finalizar SM';
      snackbar.show(<Text>{message}</Text>, { type: 'danger' });
      console.error('Erro ao finalizar SM:', err);
      if (onRefresh) {
        await onRefresh();
      }
      setReloadSmInfo((prev) => !prev);
    } finally {
      setStep('INTEGRATION');
      if (getLoad) await getLoad();
      setReloadSmInfo((prev) => !prev);
      setLoading(false);
    }
  };

  const handleCancelSm = async () => {
    setLoading(true);
    try {
      const codPreSM = preSMData?.presm?.codPreSM;
      const codSM = preSMData?.presm?.codSM;
      const travelId = travelData?.id;

      if (!travelId) {
        snackbar.show(<Text>ID da viagem não encontrado.</Text>, { type: 'danger' });
        return;
      }

      let resp;
      let isPreSM = false;

      if (codPreSM !== null && codSM === null) {
        // É uma Pré‑SM
        resp = await cooperplaceApi.delete(`/presm/cancelPreSm/${travelId}`);
        isPreSM = true;
      } else if (codSM !== null) {
        // É uma SM
        resp = await cooperplaceApi.delete(`/presm/${travelId}`);
      } else {
        snackbar.show(
          <Text>Não foi possível identificar o tipo da SM/Pré‑SM para cancelamento.</Text>,
          { type: 'danger' },
        );
        return;
      }
      const cancelType = isPreSM ? 'Pré‑SM' : 'SM';
      snackbar.show(<Text>{`${cancelType} cancelada com sucesso.`}</Text>, { type: 'success' });

      const list = resp.data.presmList || [];
      setSmList(list);

      const nextSM = list.find((sm) => sm.status !== 'Cancelada') || null;
      setSelectedSM(nextSM);

      if (!nextSM) {
        setStep('INTEGRATION');
      }

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Erro ao cancelar SM:', err);
      const message = err?.response?.data?.message || err?.message || 'Erro ao cancelar';
      const cancelType =
        preSMData?.presm?.codPreSM !== null && preSMData?.presm?.codSM === null ? 'Pré‑SM' : 'SM';
      snackbar.show(<Text>{`Erro ao cancelar ${cancelType}: ${message}`}</Text>, {
        type: 'danger',
      });
      if (onRefresh) {
        await onRefresh();
      }
      setReloadSmInfo((prev) => !prev);
    } finally {
      setStep('INTEGRATION');
      if (getLoad) await getLoad();
      setReloadSmInfo((prev) => !prev);
      setLoading(false);
    }
  };

  const actions = useMemo(() => {
    const SLUG_NOT_CREATED = ['CANCELLED_SM', 'CANCELLED_PRE_SM', 'FINISHED_SM', 'CANCELLED_SM'];
    const SLUG_PRESM_CREATED = [
      'CREATED_PRE_SM',
      'ERROR_CANCEL_PRE_SM',
      'ERROR_EFFECT_PRE_SM',
      'ERROR_CREATED_PRE_SM',
    ];
    const SLUG_SM_CREATED = ['CREATED_SM', 'ERROR_CANCEL_SM', 'ERROR_FINISH_SM', 'EFFECT_PRE_SM'];
    const SLUG_ERROR_CREATED = ['ERROR_CREATED_PRE_SM'];
    const slug = preSMData?.presm?.statusPreSM?.slug;

    if (SLUG_SM_CREATED.includes(slug)) {
      return (
        <>
          <Button
            variant="danger"
            onClick={handleCancelSm}
            loading={loading}
            disabled={loading}
            style={{ marginRight: 12 }}
          >
            Cancelar SM
          </Button>
          <Button variant="success" onClick={handleFinalizeSm} loading={loading} disabled={loading}>
            Finalizar SM
          </Button>
        </>
      );
    }

    if (SLUG_NOT_CREATED.includes(slug)) {
      return (
        <Button
          variant="primary"
          onClick={handleGoToConfirmation}
          loading={loading}
          disabled={loading}
        >
          Solicitar SM
        </Button>
      );
    }

    if (SLUG_ERROR_CREATED.includes(slug)) {
      return (
        <Button
          variant="primary"
          onClick={handleConfirmationComplete}
          loading={loading}
          disabled={loading}
        >
          Reenviar
        </Button>
      );
    }

    if (SLUG_PRESM_CREATED.includes(slug)) {
      return (
        <>
          <Button
            variant="danger"
            onClick={handleCancelSm}
            loading={loading}
            disabled={loading}
            style={{ marginRight: 12 }}
          >
            Cancelar Pré-SM
          </Button>
          <Button variant="success" onClick={handleEffectSm} loading={loading} disabled={loading}>
            Efetivar Pré-SM
          </Button>
        </>
      );
    }

    return (
      <Button
        variant="primary"
        onClick={handleGoToConfirmation}
        loading={loading}
        disabled={loading}
      >
        Solicitar SM
      </Button>
    );
  }, [
    preSMData,
    selectedSM,
    loading,
    handleConfirmationComplete,
    handleGoToConfirmation,
    handleCancelSm,
    handleEffectSm,
    handleFinalizeSm,
  ]);

  const tractionTypeBadge = useMemo(() => {
    return renderBadge(tractionType);
  }, [tractionType]);

  return (
    <S.StyledModal show={open} onHide={onClose} scrollable centered>
      {(loading || !loadingDataReady) && (
        <div
          style={{
            position: 'absolute',
            zIndex: 9999,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      <Modal.Header closeButton>
        <Text type="header">
          {step === 'CONFIRM' ? 'Confirmação de envio de SM' : 'Solicitação de monitoramento'}
        </Text>
      </Modal.Header>

      <Modal.Body>
        {step === 'CONFIRM' ? (
          <>
            <S.FieldWrapper>
              <div className="label">Tipo de solicitação de monitoramento</div>
              <select
                className={`form-control ${smTypeError ? 'is-invalid' : ''}`}
                value={
                  travelData?.securityProfile?.code
                    ? determineSmType(travelData?.securityProfile?.code)
                    : ''
                }
                disabled
              >
                <option value="">Sem registro! Cadastre um perfil de segurança.</option>
                <option value="risco">SM de Risco</option>
                <option value="logistica">SM Logística</option>
              </select>
              {smTypeError && (
                <div className="invalid-feedback d-block">
                  Perfil de segurança obrigatório para envio da SM.
                </div>
              )}
            </S.FieldWrapper>

            <S.ConfirmationWrapper>
              <S.ConfirmationGrid>
                <S.FieldWrapper>
                  <div className="label">Filial Raster</div>
                  <div className="value">{rasterBranch}</div>
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Perfil de segurança</div>
                  <div className="value">
                    {travelData?.securityProfile?.name || 'Não informado'} –{' '}
                    {determineSmType(travelData?.securityProfile?.code) === 'logistica'
                      ? 'SM Logística'
                      : 'SM de Risco'}
                  </div>
                </S.FieldWrapper>

                {origins.map((origin, idx) => (
                  <S.FieldWrapper key={`origin-${idx}`}>
                    {renderPointBlock(`Coleta ${idx + 1}`, {
                      city: { name: extractCityName(origin), province: extractProvinceUF(origin) },
                      start_schedule: origin.startSchedule,
                      scheduled_time: origin.scheduledTime,
                    })}
                  </S.FieldWrapper>
                ))}

                {destinations.map((dest, idx) => (
                  <S.FieldWrapper key={`dest-${idx}`}>
                    {renderPointBlock(parsePointLabel(dest.action, idx), {
                      city: { name: extractCityName(dest), province: extractProvinceUF(dest) },
                      start_schedule: dest.startSchedule,
                      scheduled_time: dest.scheduledTime,
                    })}
                  </S.FieldWrapper>
                ))}

                <S.FieldWrapper>
                  <div className="label">Placa Veículo tração</div>
                  <div className="value">{tractionVehicle?.plate || NI}</div>
                  {tractionTypeBadge}
                </S.FieldWrapper>

                {wagonPlates.map((plate, idx) => (
                  <S.FieldWrapper key={`wagon-${idx}`}>
                    <div className="label">Placa Implemento {idx + 1}</div>
                    <div className="value">{plate}</div>
                    {tractionTypeBadge}
                  </S.FieldWrapper>
                ))}

                <S.FieldWrapper>
                  <div className="label">Gestor</div>
                  <div className="value">{manager}</div>
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Motorista</div>
                  <div className="value">{`${driverCpf} ${driverName}`}</div>
                  {tractionTypeBadge}
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Transportador</div>
                  <div className="value">{`${shipperCnpj} ${shipperName}`}</div>
                  {tractionTypeBadge}
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Produto / NCM</div>
                  <div className="value">{productDisplay}</div>
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Valor mercadoria</div>
                  <div className="value">{formattedCargoValue}</div>
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Veículos escolta</div>
                  <div className="value">
                    {Array.isArray(escorts) && escorts.length > 0
                      ? escorts.map((e, i) => (
                          <div key={`escort-vehicle-${i}`}>{e.vehicle || NI}</div>
                        ))
                      : escortVehicle}
                  </div>
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Agentes escolta</div>
                  <div className="value">
                    {Array.isArray(escorts) && escorts.length > 0
                      ? escorts.map((e, i) => (
                          <div key={`escort-agent-${i}`}>
                            {`${e.agentName || NI} – ${e.agentCpf || NI}`}
                          </div>
                        ))
                      : escortAgent}
                  </div>
                </S.FieldWrapper>

                <S.FieldWrapper>
                  <div className="label">Iscas</div>
                  <div className="value">
                    {Array.isArray(baits) && baits.length > 0
                      ? baits.map((b, i) => <div key={`bait-${i}`}>{b?.name || NI}</div>)
                      : NI}
                  </div>
                </S.FieldWrapper>
              </S.ConfirmationGrid>
            </S.ConfirmationWrapper>
          </>
        ) : (
          <S.Section>
            <S.SmAttemptsSection>
              <Text type="label" color="gray">
                SOLICITAÇÕES DE MONITORAMENTO (TENTATIVAS)
              </Text>
              <S.SmListBox>
                {smList.filter((sm) => sm.status !== 'Desconhecido').length === 0 ? (
                  <Text type="regular" color="gray">
                    Nenhuma SM encontrada.
                  </Text>
                ) : (
                  smList.map((sm) => {
                    const isSelected = sm.id === visualizedSM?.id;
                    const prefix = getPrefixForAttempt(sm.status, sm.isPre);
                    const verb = getVerbForStatus(sm.status);
                    const dateStr = moment(sm.createdAt).format('DD/MM/YYYY [às] HH:mm');

                    return (
                      <S.SmListItem
                        key={sm.id}
                        className={`${isSelected ? 'selected' : ''} ${getStatusColorClass(
                          sm.status,
                        )}`}
                        onClick={() => {
                          setVisualizedSM(sm);
                        }}
                      >
                        <div className="sm-status-inline">
                          <span className="sm-status-title">{sm.status}</span>
                          <span className="sm-status-text">
                            {prefix} de {sm.type} – <strong>{sm.code}</strong> {verb} {sm.createdBy}{' '}
                            em {dateStr}
                          </span>
                        </div>
                      </S.SmListItem>
                    );
                  })
                )}
              </S.SmListBox>
            </S.SmAttemptsSection>

            <S.SmEventsSection>
              <Text type="label" color="gray">
                HISTÓRICO
              </Text>
              <S.SmEventsBox>
                {Array.isArray(preSMData?.logs) && preSMData.logs.length > 0 ? (
                  preSMData.logs.map((log, i) => {
                    const messageText = log.message || log.log_status || 'Sem mensagem';
                    const createdBy = log.user?.username || 'Usuário não informado';
                    const createdAt = moment(log.createdAt).format('DD/MM/YYYY [às] HH:mm');
                    const code = log.cod_sm || log.cod_pre_sm;

                    let Icon = FileText;
                    let iconColor = '#6c757d';

                    const msgLower = messageText.toLowerCase();

                    if (
                      msgLower.includes('sucesso') ||
                      msgLower.includes('efetivada') ||
                      msgLower.includes('finalizada')
                    ) {
                      Icon = CheckCircle;
                      iconColor = '#28a745';
                    } else if (
                      msgLower.includes('erro') ||
                      msgLower.includes('falha') ||
                      msgLower.includes('fracassada')
                    ) {
                      Icon = AlertTriangle;
                      iconColor = '#ffc107';
                    } else if (
                      msgLower.includes('cancelada') ||
                      msgLower.includes('cancelado') ||
                      msgLower.includes('cancel')
                    ) {
                      Icon = XCircle;
                      iconColor = '#dc3545';
                    } else if (msgLower.includes('impress')) {
                      Icon = FileText;
                      iconColor = '#17a2b8';
                    }
                    const isSelected = visualizedSM?.id === log.id;
                    return (
                      <S.SmListItem
                        key={i}
                        onClick={() => setVisualizedSM(log)}
                        className={isSelected ? 'selected' : ''}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="log-content">
                          <Icon
                            size={16}
                            style={{ marginRight: 6, color: iconColor, verticalAlign: 'middle' }}
                          />
                          {messageText} – {code && <strong>{code}</strong>} Por {createdBy} em{' '}
                          {createdAt}
                        </span>
                      </S.SmListItem>
                    );
                  })
                ) : (
                  <Text type="regular" color="gray">
                    Nenhum histórico encontrado para esta SM.
                  </Text>
                )}
              </S.SmEventsBox>
            </S.SmEventsSection>

            <S.ApiSection>
              <Text type="label" color="gray">
                REQUISIÇÃO
              </Text>
              <S.JsonBox>
                {visualizedSM?.request ? (
                  <pre>
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(visualizedSM.request), null, 2);
                      } catch (e) {
                        return visualizedSM.request; // mostra raw mesmo se não for JSON válido
                      }
                    })()}
                  </pre>
                ) : (
                  <Text type="regular" color="gray">
                    Sem requisição registrada.
                  </Text>
                )}
              </S.JsonBox>
            </S.ApiSection>

            <S.ApiSection>
              <Text type="label" color="gray">
                RETORNO
              </Text>
              <S.JsonBox>
                {visualizedSM?.response ? (
                  <pre>{JSON.stringify(JSON.parse(visualizedSM.response), null, 2)}</pre>
                ) : (
                  <Text type="regular" color="gray">
                    Sem resposta registrada.
                  </Text>
                )}
              </S.JsonBox>
            </S.ApiSection>
          </S.Section>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <div>
          <Button
            variant="secondary"
            onClick={step === 'CONFIRM' ? handleBackToIntegration : onClose}
          >
            {step === 'CONFIRM' ? 'Voltar' : 'Fechar'}
          </Button>
        </div>
        <div className="d-flex gap-2">
          {step === 'INTEGRATION' && actions}
          {step === 'CONFIRM' && (
            <Button
              variant="success"
              onClick={handleConfirmationComplete}
              loading={loading}
              disabled={loading}
            >
              Enviar Pré-SM
            </Button>
          )}
        </div>
      </Modal.Footer>
    </S.StyledModal>
  );
}
